import { ReactNode } from 'react';
import { Box } from '@mui/material';
import { getCrmColors } from '../kanban-board/crm-theme';

interface Props {
    cc: ReturnType<typeof getCrmColors>;
    children: ReactNode;

    /// Ocupa a altura do pai, com a grade rolando por dentro; só para a Agenda.
    fill?: boolean;
}

/// O FullCalendar traz CSS com cores fixas; repintar por variável alinha a agenda ao tema.
export const AgendaStyles = ({ cc, children, fill = false }: Props) => (
    <Box
        sx={{
            background: cc.cardBg,
            border: `1px solid ${cc.border}`,
            borderRadius: 3,
            p: 1.5,

            /// A grade rola dentro do quadro, que ocupa a altura do pai: pixels errariam paddings.
            ...(fill && {
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',

                '& > .fc': { flex: 1, minHeight: 0 },

                /// Altura da faixa de meia hora: sem fixar, o dia inteiro daria ~15px por faixa.
                '& .fc .fc-timegrid-slot': { height: '3.2em' },
            }),

            '& .fc': {
                '--fc-border-color': cc.border,
                '--fc-page-bg-color': 'transparent',
                '--fc-neutral-bg-color': cc.countBg,
                '--fc-today-bg-color': cc.accentSoftBg,
                '--fc-now-indicator-color': cc.eventDanger,
                color: cc.textPrimary,
                fontSize: '0.85rem',
            },

            /// Só a hora cheia leva rótulo, e ele fica alinhado com a linha.
            '& .fc .fc-timegrid-slot-label': { verticalAlign: 'middle' },

            /// Meia hora mais discreta que a cheia: 48 linhas iguais viram gradeado.
            '& .fc .fc-timegrid-slot-minor': { borderTopStyle: 'dotted', opacity: 0.6 },

            '& .fc .fc-toolbar-title': {
                fontSize: '1rem',
                fontWeight: 700,
                color: cc.textPrimary,
                '&::first-letter': { textTransform: 'uppercase' },
            },

            '& .fc .fc-button': {
                background: cc.surfaceBg,
                border: `1px solid ${cc.border}`,
                color: cc.textSecondary,
                boxShadow: 'none',
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                padding: '4px 10px',
            },
            '& .fc .fc-button:hover': {
                background: cc.countBg,
                borderColor: cc.borderStrong,
                color: cc.textPrimary,
            },
            '& .fc .fc-button-primary:not(:disabled).fc-button-active': {
                background: cc.accent,
                borderColor: cc.accent,
                color: '#fff',
            },
            '& .fc .fc-button:focus': { boxShadow: 'none' },
            '& .fc .fc-button:disabled': { opacity: 0.45 },

            '& .fc .fc-col-header-cell-cushion, & .fc .fc-timegrid-axis-cushion, & .fc .fc-timegrid-slot-label-cushion':
            {
                color: cc.textSecondary,
                fontWeight: 600,
                fontSize: '0.78rem',
                textDecoration: 'none',
            },

            /// Cabeçalho do dia com mais respiro: é a âncora de leitura da semana.
            '& .fc .fc-col-header-cell': { padding: '6px 0' },

            '& .fc .fc-daygrid-day-number': {
                color: cc.textSecondary,
                fontSize: '0.76rem',
                textDecoration: 'none',
            },

            '& .fc .fc-event': {
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                overflow: 'hidden',
            },

            '& .fc .fc-timegrid-now-indicator-line': { borderWidth: '2px' },
        }}
    >
        {children}
    </Box>
);
